import { useEffect, useMemo, useRef, useState } from 'react';
import {
  computeDisplayedPotentialWin,
  getDisplayedFinalOdds,
} from '../_helpers/betCartStakeLimits';
import { toDisplayOddsDecimal } from '../_helpers/oddsFormat';

const roundOdds = (value) => toDisplayOddsDecimal(value) ?? 0;

const optionalNumber = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const optionalString = (value) => (
  value === undefined || value === null || value === '' ? null : String(value)
);

const buildSportsnewSelection = (selection) => ({
  oddStateId: Number(selection.oddStateId ?? selection.odds_id ?? selection.id),
  providerOddId: optionalString(selection.providerOddId ?? selection.provider_odd_id),
  oddIdentityKey: optionalString(selection.oddIdentityKey ?? selection.odd_identity_key),
  shortOddId: optionalString(selection.shortOddId ?? selection.short_odd_id),
  canonicalSelectionKey: optionalString(selection.canonicalSelectionKey ?? selection.canonical_selection_key),
  canonicalLineKey: optionalString(selection.canonicalLineKey ?? selection.canonical_line_key),
  canonicalPairKey: optionalString(selection.canonicalPairKey ?? selection.canonical_pair_key),
  points: optionalNumber(selection.points ?? selection.line),
  line: optionalNumber(selection.line ?? selection.points),
  selectionLine: optionalString(selection.selectionLine ?? selection.selection_line),
  groupingKey: optionalString(selection.groupingKey ?? selection.grouping_key),
  expectedPriceDecimal: roundOdds(selection.odds ?? selection.priceDecimal ?? selection.price),
});

const buildPreviewPayload = ({
  selections,
  betType,
  bettingCategory,
  displayScope,
  stake,
  isSportsnew,
  betMode,
}) => isSportsnew ? ({
  bet_type: betType,
  betting_category: bettingCategory,
  displayScope,
  scope: displayScope,
  betMode,
  stake: Number(stake) > 0 ? Number(stake) : 1000,
  selections: selections.map(buildSportsnewSelection),
}) : ({
  bet_type: betType,
  betting_category: bettingCategory,
  displayScope,
  scope: displayScope,
  stake: 0,
  selections: selections.map((selection) => ({
    match_id: selection.match_id,
    market_id: selection.market_id,
    odds_id: selection.odds_id,
    requested_odds: Number(selection.odds),
  })),
});

const buildPreviewKey = ({ selections, betType, bettingCategory, displayScope, isAuthenticated, isSportsnew, betMode }) => {
  if (!isAuthenticated || betType !== 'COMBO' || selections.length < 2) return '';
  return [
    bettingCategory,
    displayScope,
    isSportsnew ? 'sportsnew' : 'legacy',
    betMode,
    betType,
    ...selections.map((selection) => [
      selection.match_id,
      selection.market_id,
      selection.odds_id,
      selection.shortOddId,
      selection.canonicalSelectionKey,
      selection.canonicalLineKey,
      selection.canonicalPairKey,
      selection.line,
      selection.points,
      roundOdds(selection.odds || 0),
    ].join(':')),
  ].join('|');
};

const toFiniteNumber = (value, fallback = 0) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

export default function useBetQuotePreview({
  SportsService,
  selections,
  betType,
  bettingCategory,
  displayScope,
  stake,
  totalOdds,
  bonusMultiplier,
  bettingLimits,
  isAuthenticated,
  isSportsnew = false,
  betMode = 'prematch',
}) {
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const requestSeqRef = useRef(0);
  const previewKey = useMemo(
    () => buildPreviewKey({ selections, betType, bettingCategory, displayScope, isAuthenticated, isSportsnew, betMode }),
    [selections, betType, bettingCategory, displayScope, isAuthenticated, isSportsnew, betMode]
  );

  useEffect(() => {
    if (!previewKey || !SportsService?.previewBet) {
      setQuote(null);
      setQuoteLoading(false);
      return undefined;
    }

    const seq = ++requestSeqRef.current;
    const timer = setTimeout(async () => {
      setQuoteLoading(true);
      try {
        const response = await SportsService.previewBet(
          buildPreviewPayload({
            selections,
            betType,
            bettingCategory,
            displayScope,
            stake,
            isSportsnew,
            betMode,
          })
        );
        if (seq !== requestSeqRef.current) return;
        if (response?.status && response?.data) {
          setQuote(response.data);
        } else {
          setQuote(null);
        }
      } catch (_error) {
        if (seq === requestSeqRef.current) {
          setQuote(null);
        }
      } finally {
        if (seq === requestSeqRef.current) {
          setQuoteLoading(false);
        }
      }
    }, 180);

    return () => {
      clearTimeout(timer);
    };
  }, [SportsService, previewKey, selections, betType, bettingCategory, displayScope, stake, isSportsnew, betMode]);

  const displayedBaseOdds = useMemo(() => {
    const quotedBaseOdds = toFiniteNumber(quote?.base_odds, 0);
    if (quotedBaseOdds > 0) {
      return roundOdds(quotedBaseOdds);
    }
    return roundOdds(totalOdds || 0);
  }, [quote?.base_odds, totalOdds]);
  const displayedBonusMultiplier = useMemo(() => {
    if (betType !== 'COMBO') return 1;
    return roundOdds(toFiniteNumber(quote?.bonus_multiplier, bonusMultiplier || 1));
  }, [betType, quote?.bonus_multiplier, bonusMultiplier]);
  const comboOddsAdjust = useMemo(
    () => roundOdds(toFiniteNumber(quote?.combo_odds_adjust, 0)),
    [quote?.combo_odds_adjust]
  );

  const displayedFinalOdds = useMemo(() => {
    if (betType === 'COMBO') {
      const quotedFinalOdds = toFiniteNumber(quote?.total_odds, 0);
      if (quotedFinalOdds > 0) {
        return roundOdds(quotedFinalOdds);
      }
    }
    return getDisplayedFinalOdds({ betType, selections, totalOdds, bonusMultiplier });
  }, [betType, quote?.total_odds, selections, totalOdds, bonusMultiplier]);

  const displayedPotentialWin = useMemo(() => computeDisplayedPotentialWin({
    stake,
    displayedFinalOdds,
    bettingLimits,
  }), [stake, displayedFinalOdds, bettingLimits]);

  const shouldShowFinalOdds = useMemo(() => {
    if (betType !== 'COMBO') return false;
    return (
      Math.abs(displayedFinalOdds - displayedBaseOdds) > 0.001
      || Math.abs(displayedBonusMultiplier - 1) > 0.001
      || Math.abs(comboOddsAdjust) > 0.001
      || quoteLoading
    );
  }, [
    betType,
    displayedFinalOdds,
    displayedBaseOdds,
    displayedBonusMultiplier,
    comboOddsAdjust,
    quoteLoading,
  ]);

  return {
    quote,
    quoteLoading,
    displayedBaseOdds,
    displayedBonusMultiplier,
    comboOddsAdjust,
    displayedFinalOdds,
    displayedPotentialWin,
    shouldShowFinalOdds,
  };
}
